import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import L from "leaflet";
import api from "../lib/api";
import { MapContainer, TileLayer, Circle, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Map as MapIcon, ArrowLeft, Loader2, MapPin, Pencil, ClipboardList, Users, Move, Save, X, UserPlus } from "lucide-react";
import { SiteDialog } from "./CantieriPage";
import { QuickAssignDialog } from "../components/QuickAssignDialog";

const ACTIVE_COLOR = "#059669";
const INACTIVE_COLOR = "#d97706";
const PENDING_COLOR = "#376DB3";

const dotIcon = (color, pending) => L.divIcon({
    className: "",
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -10],
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.35);cursor:grab;${pending ? "outline:3px solid rgba(55,109,179,.35);" : ""}"></div>`,
});

function FitBounds({ points }) {
    const map = useMap();
    useEffect(() => {
        if (points.length > 0) {
            map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [50, 50], maxZoom: 15 });
        }
    }, [points, map]);
    return null;
}

export default function CantieriMappaPage() {
    const [sites, setSites] = useState(null);
    const [editSite, setEditSite] = useState(null);
    const [assignSiteId, setAssignSiteId] = useState(null);
    const [pending, setPending] = useState(null); // { site, lat, lng }
    const [saving, setSaving] = useState(false);
    const [dragKey, setDragKey] = useState(0);

    const load = useCallback(() => {
        api.get("/presenze/worksites")
            .then((r) => setSites(r.data))
            .catch(() => { toast.error("Errore nel caricamento dei cantieri"); setSites([]); });
    }, []);

    useEffect(() => { load(); }, [load]);

    const located = useMemo(() => (sites || []).filter((s) => s.lat != null && s.lng != null), [sites]);
    const missing = (sites || []).length - located.length;

    const cancelPending = () => { setPending(null); setDragKey((k) => k + 1); };

    const savePending = async () => {
        if (!pending) return;
        const s = pending.site;
        setSaving(true);
        try {
            await api.patch(`/presenze/worksites/${s.id}`, {
                name: s.name, client_name: s.client_name || null, address: s.address || null,
                lat: pending.lat, lng: pending.lng, radius_m: s.radius_m || 50, active: s.active !== false,
            });
            toast.success(`Posizione di ${s.name} aggiornata`);
            setPending(null);
            load();
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Errore nel salvataggio della posizione");
        } finally { setSaving(false); }
    };

    const posOf = (s) => (pending?.site.id === s.id ? [pending.lat, pending.lng] : [s.lat, s.lng]);

    return (
        <div className="mx-auto max-w-7xl px-6 py-8" data-testid="cantieri-mappa-page">
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <MapIcon className="h-6 w-6" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl font-bold text-slate-800">Mappa cantieri</h1>
                        <p className="text-sm text-slate-500">
                            {located.length} cantieri sulla mappa con area GPS di timbratura
                            {missing > 0 && ` · ${missing} senza coordinate (non mostrati)`}
                            <span className="ml-1 inline-flex items-center gap-1 text-[#376DB3]"><Move className="h-3.5 w-3.5" /> trascina un segnaposto per correggere la posizione</span>
                        </p>
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600">
                        <span className="inline-flex items-center gap-1.5">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ background: ACTIVE_COLOR }} /> Attivo
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                            <span className="h-2.5 w-2.5 rounded-full" style={{ background: INACTIVE_COLOR }} /> Disattivo
                        </span>
                    </div>
                    <Link to="/presenze/cantieri">
                        <Button variant="outline" className="gap-2" data-testid="mappa-back-btn">
                            <ArrowLeft className="h-4 w-4" /> Elenco cantieri
                        </Button>
                    </Link>
                </div>
            </div>

            {pending && (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#376DB3]/30 bg-[#376DB3]/5 px-4 py-3" data-testid="mappa-pending-bar">
                    <div className="flex items-center gap-2 text-sm text-slate-700">
                        <Move className="h-4 w-4 text-[#376DB3]" />
                        Nuova posizione per <b>{pending.site.name}</b>:
                        <span className="font-mono-numbers text-xs text-slate-500" data-testid="mappa-pending-coords">{pending.lat.toFixed(6)}, {pending.lng.toFixed(6)}</span>
                        <span className="text-xs text-slate-500">(l'indirizzo resta invariato)</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="gap-1.5" onClick={cancelPending} disabled={saving} data-testid="mappa-pending-cancel">
                            <X className="h-3.5 w-3.5" /> Annulla
                        </Button>
                        <Button size="sm" className="gap-1.5 bg-[#376DB3] text-white hover:bg-[#2c5a96]" onClick={savePending} disabled={saving} data-testid="mappa-pending-save">
                            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Salva posizione
                        </Button>
                    </div>
                </div>
            )}

            {!sites ? (
                <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
            ) : located.length === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="mappa-empty">
                    Nessun cantiere con coordinate GPS da mostrare sulla mappa.
                </div>
            ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 shadow-sm" data-testid="mappa-container">
                    <MapContainer
                        center={[located[0].lat, located[0].lng]}
                        zoom={12}
                        style={{ height: "calc(100vh - 220px)", minHeight: "480px", zIndex: 0 }}
                        scrollWheelZoom
                    >
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <FitBounds points={located} />
                        {located.map((s) => (
                            <Circle
                                key={`r-${s.id}`}
                                center={posOf(s)}
                                radius={s.radius_m || 200}
                                pathOptions={{
                                    color: pending?.site.id === s.id ? PENDING_COLOR : s.active ? ACTIVE_COLOR : INACTIVE_COLOR,
                                    weight: 1.5,
                                    fillOpacity: 0.12,
                                }}
                            />
                        ))}
                        {located.map((s) => (
                            <Marker
                                key={`m-${s.id}-${dragKey}`}
                                position={posOf(s)}
                                draggable
                                icon={dotIcon(pending?.site.id === s.id ? PENDING_COLOR : s.active ? ACTIVE_COLOR : INACTIVE_COLOR, pending?.site.id === s.id)}
                                eventHandlers={{
                                    dragend: (e) => {
                                        const { lat, lng } = e.target.getLatLng();
                                        setPending({ site: s, lat, lng });
                                    },
                                }}
                            >
                                <Popup>
                                    <div className="min-w-[210px]" data-testid={`mappa-popup-${s.id}`}>
                                        <div className="font-bold text-slate-800">{s.name}</div>
                                        {s.client_name && <div className="text-xs text-slate-500">{s.client_name}</div>}
                                        {s.address && (
                                            <div className="mt-1 flex items-start gap-1 text-xs text-slate-600">
                                                <MapPin className="mt-0.5 h-3 w-3 flex-shrink-0" /> {s.address}
                                            </div>
                                        )}
                                        <div className="mt-1.5 text-xs text-slate-600">
                                            Raggio timbratura: <b>{s.radius_m} m</b>
                                            {" · "}
                                            <span style={{ color: s.active ? ACTIVE_COLOR : INACTIVE_COLOR, fontWeight: 700 }}>
                                                {s.active ? "ATTIVO" : "DISATTIVO"}
                                            </span>
                                        </div>
                                        {s.needs_review && (
                                            <div className="mt-1 text-[11px] font-bold text-amber-600">DA VERIFICARE</div>
                                        )}
                                        <div className="mt-1.5 flex items-center gap-3 text-[11px] text-slate-500">
                                            <span className="inline-flex items-center gap-1"><ClipboardList className="h-3 w-3" /> {s.activities_count ?? 0} attività</span>
                                            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {s.assigned_count ?? 0} dipendenti</span>
                                        </div>
                                        <div className="mt-2.5 flex items-center gap-2">
                                            <Link
                                                to={`/presenze/cantieri/${s.id}`}
                                                className="inline-flex flex-1 items-center justify-center gap-1 rounded-md bg-[#376DB3] px-2.5 py-1.5 text-xs font-semibold !text-white hover:bg-[#2c5a96]"
                                                data-testid={`mappa-detail-${s.id}`}
                                            >
                                                <ClipboardList className="h-3 w-3" /> Apri dettaglio
                                            </Link>
                                            <button
                                                type="button"
                                                onClick={() => setEditSite(s)}
                                                className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                                                data-testid={`mappa-edit-${s.id}`}
                                            >
                                                <Pencil className="h-3 w-3" /> Modifica
                                            </button>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setAssignSiteId(s.id)}
                                            className="mt-1.5 inline-flex w-full items-center justify-center gap-1 rounded-md border border-[#376DB3]/40 bg-white px-2.5 py-1.5 text-xs font-semibold text-[#376DB3] hover:bg-[#376DB3]/5"
                                            data-testid={`mappa-assign-${s.id}`}
                                        >
                                            <UserPlus className="h-3 w-3" /> Assegna dipendente
                                        </button>
                                        <div className="mt-1.5 text-[10px] text-slate-400">Trascina il segnaposto per spostare il cantiere</div>
                                    </div>
                                </Popup>
                            </Marker>
                        ))}
                    </MapContainer>
                </div>
            )}

            <SiteDialog
                open={!!editSite}
                site={editSite}
                onClose={() => setEditSite(null)}
                onSaved={() => { setEditSite(null); load(); }}
            />
            <QuickAssignDialog
                open={!!assignSiteId}
                worksiteId={assignSiteId || undefined}
                onClose={() => setAssignSiteId(null)}
                onSaved={() => { setAssignSiteId(null); load(); }}
            />
        </div>
    );
}
